<script lang="ts" setup>
import OrderForm from '@/components/features/orders/OrderForm.vue'
import CustomerDetailsForm from '@/components/features/orders/CustomerDetailsForm.vue'
import ProductDetailsForm from '@/components/features/orders/ProductDetailsForm.vue'
import { useRouter, RouterLink } from 'vue-router'
import { useOrderForm } from '@/composables/useOrderForm'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { addOrder } from '@/api/orders'
import { computed, nextTick, onMounted, ref } from 'vue'
import { getCustomerList } from '@/api/customers'
import type { Customer } from '@/types/customer-types'
import type { ErrorResponse } from '@/types/error-types'
import { Unit } from '@/constants/dimension'

const router = useRouter()
const {
  values,
  loading,
  handleSubmit,
  setErrors,
  formType,
  showToast,
  newProduct,
  removeProduct,
  productFields,
  setFieldValue
} = useOrderForm('add')

const customerList = ref<Customer[]>([])
function handleValuesUpdate(updatedValues: any) {
  Object.assign(values, updatedValues)
}

const showRemoveButton = computed(() => productFields.value.length > 1)

function handleFieldUpdate(fieldData: any) {
  if (fieldData.orderProducts) {
    setFieldValue('orderProducts', Object.values(fieldData.orderProducts))
  } else {
    Object.entries(fieldData).forEach(([key, value]) => {
      setFieldValue(key, value)
    })
  }
}
const submit = handleSubmit(
  async () => {
    loading.value = true

    const formValues = {
      ...values,
      orderProducts: values?.orderProducts?.map((product: any) => ({
        ...product,
        pricePerUnitAmount: product.costPerUnitAmount,
        pricePerUnitCurrency: product.costPerUnitCurrency,
      })),
    }

    try {
      const response = await addOrder(formValues)
      loading.value = false
      showToast('success', 'Order Added!', 'You have successfully added a new order.')
      router.push(`/orders/${response.id}/addPackingList`)
    } catch (error: any) {
      loading.value = false
      const errorMessages =
        error.response?.data?.errors?.map((err: any) => err.description).join('\n') ||
        'Unable to add a new order.'
      showToast('error', 'An error occurred!', errorMessages)
    }
  },
  (errors: Record<string, string>) => {
  loading.value = false

  const errorObjects = Object.entries(errors.errors).map(([field, message]) => ({
    code: field,
    description: message,
  }))

  if(errorObjects.length > 0) {
    const firstError = errorObjects[0]
    const errorMessage = ` ${firstError.description}`
    showToast('error', 'Validation Error', errorMessage)
    return
  }
}
)

function addProductWithDefaults() {
  newProduct()
  nextTick().then(() => {
    const updatedProducts = values.orderProducts?.map((product: any) => ({
      ...product,
      totalLengthUnit: Unit.Metres,
      totalLengthValue: product.totalLengthUnit || 0,
    })) ?? []
    setFieldValue('orderProducts', updatedProducts)
  })
}

onMounted(async () => {
 const defaultLengthUnit = Unit.Metres
  if (productFields.value.length === 0) {
    newProduct()
    await nextTick()
    const updatedProducts = values.orderProducts?.map((product: any) => ({
      ...product,
      totalLengthUnit: defaultLengthUnit
    })) ?? []

    setFieldValue('orderProducts', updatedProducts)
  }

  customerList.value = await getCustomerList()
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <RouterLink to="/orders" class="flex items-center gap-x-2 group hover:text-primary-default">
      <Icon
        icon="material-symbols:arrow-back-rounded"
        class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </RouterLink>

    <h1 class="font-semibold text-2xl">Add New Order</h1>

    <Button label="Save Order" @click="submit">
      <template #icon>
        <Icon icon="material-symbols:save" class="text-xl ml-3 order-1" />
      </template>
    </Button>
  </div>
  <div class="bg-white rounded-xl pb-2">
    <OrderForm
      :type="formType"
      :values="values"
      :onSubmit="handleSubmit"
      :setErrors="setErrors"
      @update:values="handleValuesUpdate"
    />
    <CustomerDetailsForm
      :type="formType"
      :values="values"
      :onSubmit="handleSubmit"
      :setErrors="setErrors"
      :customer-list="customerList"
      @update:field="handleFieldUpdate"
    />
    <template v-for="(product, index) in productFields" :key="product.key">
      <p class="text-lg font-bold ml-7">Product Details ({{ productFields.length }})</p>
      <ProductDetailsForm
        :customer-list="customerList"
        :values="values"
        :type="formType"
        :formPath="`${index}`"
        :setErrors="setErrors"
        :productFields="values?.orderProducts[index]"
        :removeProduct="() => removeProduct(index)"
        :showRemoveButton="showRemoveButton"
        :newProduct="addProductWithDefaults"
        @update-field="handleFieldUpdate"
      />
    </template>
    <div class="p-5">
      <FormTextArea
        label="Delivery Information"
        inputName="deliveryInformation"
        id="deliveryInformation"
      />
    </div>
  </div>
</template>
